import { type Column } from '@tanstack/react-table';
import { useForm, type Control } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';

import { CustomIntegrationProviderEnum } from 'src/graphql/schema';
import { TableRow, TableCell } from '@/components/ui/table';
import { Form, FormField, FormItem, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { CODECOMMIT_URL_PATTERN, S3_BUCKET_NAME_PATTERN } from 'src/utils/validation/validation-patterns';

import CustomAssetTable, { type ICustomAssetTableRow, type Props as CustomAssetTableProps } from './CustomAssetTable';

const assetSchema = z.object({
  title: z
    .string()
    .optional()
    .refine((value) => (Boolean(value) && value?.trim()) !== '', 'Title is required'),
  url: z
    .string()
    .min(1, 'URL is required')
    .refine((url) => url.trim() !== '', 'URL is required'),
});

const codecommitSchema = z.object({
  url: z.string().min(1, 'URL is required').regex(CODECOMMIT_URL_PATTERN, 'Invalid URL'),
});

const awsSchema = z.object({
  title: z.string().optional(),
  url: z.string().superRefine((value, ctx) => {
    if (value === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Bucket name is required' });
    } else if (/[/:]/.test(value) || /\.amazonaws\.com$/i.test(value)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Enter the bucket name only (e.g. my-bucket), not a URL',
      });
    } else if (!S3_BUCKET_NAME_PATTERN.test(value)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid bucket name' });
    }
  }),
});

const providerSchemas: Partial<Record<CustomIntegrationProviderEnum, z.ZodTypeAny>> = {
  [CustomIntegrationProviderEnum.Codecommit]: codecommitSchema,
  [CustomIntegrationProviderEnum.Aws]: awsSchema,
};

type AssetFormValues = z.infer<typeof assetSchema>;

type CustomAssetFormRowProps = {
  tableColumns: Column<ICustomAssetTableRow, unknown>[];
  control: Control<AssetFormValues>;
  onAddClick: () => void;
  isSubmitting: boolean;
  isValid: boolean;
};

const CustomAssetFormRow = ({ tableColumns, control, onAddClick, isSubmitting, isValid }: CustomAssetFormRowProps) => {
  return (
    <TableRow>
      {tableColumns.map(({ id, columnDef }) => {
        if (id === 'operation') {
          return (
            <TableCell key={id} className="w-18">
              <Button
                variant="outline"
                size="icon"
                onClick={onAddClick}
                isLoading={isSubmitting}
                disabled={isSubmitting || !isValid}
              >
                <Plus />
              </Button>
            </TableCell>
          );
        }

        if (id === 'title' || id === 'url') {
          return (
            <TableCell key={id}>
              <FormField
                control={control}
                name={id as keyof AssetFormValues}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Input {...field} placeholder={columnDef.meta?.placeholder} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </TableCell>
          );
        }

        return <TableCell key={id} />;
      })}
    </TableRow>
  );
};

type Props = CustomAssetTableProps & {
  onAdd: (item: { title?: string; url: string }) => void;
  customValidator?: (asset: { title?: string; url: string }, currentItems: ICustomAssetTableRow[]) => Promise<void>;
};

const CustomAssetFormTable = (props: Props) => {
  const { provider, dataSource, onAdd, customValidator, className, classes } = props;
  const form = useForm<AssetFormValues>({
    resolver: zodResolver(providerSchemas[provider] ?? assetSchema),
    defaultValues: { title: '', url: '' },
    mode: 'onChange',
  });

  const saveAsset = async (values: AssetFormValues) => {
    try {
      await customValidator?.({ url: values.url, title: values?.title }, dataSource);
      await onAdd({ title: values?.title, url: values.url });
      form.reset();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  return (
    <Form {...form}>
      <CustomAssetTable
        formRow={({ tableColumns }) => (
          <CustomAssetFormRow
            tableColumns={tableColumns}
            control={form.control}
            onAddClick={form.handleSubmit(saveAsset)}
            isSubmitting={form.formState.isSubmitting}
            isValid={form.formState.isValid}
          />
        )}
        provider={provider}
        dataSource={dataSource}
        classes={classes}
        className={className}
        {...('removeButton' in props ? { removeButton: props.removeButton } : { onRemove: props.onRemove })}
      />
    </Form>
  );
};

export default CustomAssetFormTable;
